import { useState, useMemo, useCallback } from "react";
import { mutate } from "swr";
import { IconPercentage, IconCopy, IconDeviceFloppy } from "@tabler/icons-react";
import type { ProductVariantDto } from "../../../../api";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Input } from "../../../../components/common/input";
import { Switch } from "../../../../components/common/switch";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import { updateMyProductPricing } from "../../../api_client";
import type { UpdateVariantPricingPayload } from "../../../api_client";

function calcPrice(supplierCost: number, margin: number, roundUp: boolean): number {
  const raw = supplierCost * (1 + margin / 100);
  return roundUp ? Math.ceil(raw / 10) * 10 - 0.01 : Math.round(raw * 100) / 100;
}

function variantLabel(v: ProductVariantDto, index: number): string {
  const parts: string[] = [];
  if (v.defaultCode) parts.push(v.defaultCode);
  if (v.model) parts.push(v.model);
  if (v.displayName && parts.length === 0) parts.push(v.displayName);
  if (parts.length === 0) parts.push(`Variant ${index + 1}`);
  return parts.join(" — ");
}

function getTierLabel(minQty: number): string {
  return `${minQty}+ units`;
}

interface VariantTierState {
  minQty: number;
  currentPrice: number | null;
  supplierCost: number | null;
  margin: string;
  directPrice: string;
}

interface BuyerPricingPanelProps {
  templateId: number;
  variants: ProductVariantDto[];
}

export function BuyerPricingPanel({ templateId, variants }: BuyerPricingPanelProps) {
  if (!variants.length) {
    return (
      <div className="rounded-2xl border border-borderDefault bg-bgWhite p-6">
        <Text variant="body" tone="muted">No variants to price.</Text>
      </div>
    );
  }

  // Keyed on the variant set: tierStates is seeded from `variants` in a
  // useState initializer, so a changed variant list must remount the form
  // rather than leave stale state behind.
  return (
    <BuyerPricingForm
      key={variants.map((v) => v.id).join(",")}
      templateId={templateId}
      variants={variants}
    />
  );
}

interface BuyerPricingFormProps {
  templateId: number;
  variants: ProductVariantDto[];
}

function BuyerPricingForm({ templateId, variants }: BuyerPricingFormProps) {
  const [roundUp, setRoundUp] = useState(false);
  const [globalMargin, setGlobalMargin] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [tierStates, setTierStates] = useState<Record<number, VariantTierState[]>>(() => {
    const initial: Record<number, VariantTierState[]> = {};
    for (const v of variants) {
      const tiers = [...(v.pricelistItems ?? [])].sort(
        (a, b) => (a.minQuantity ?? 1) - (b.minQuantity ?? 1),
      );
      const supplierCost = v.sellerPrice ?? null;

      if (tiers.length > 0) {
        initial[v.id] = tiers.map((t) => ({
          minQty: t.minQuantity ?? 1,
          currentPrice: t.fixedPrice ?? null,
          supplierCost,
          margin: "",
          directPrice: String(t.fixedPrice ?? ""),
        }));
      } else {
        initial[v.id] = [
          { minQty: 1, currentPrice: null, supplierCost, margin: "", directPrice: "" },
        ];
      }
    }
    return initial;
  });

  const updateTier = useCallback(
    (variantId: number, minQty: number, field: "margin" | "directPrice", value: string) => {
      setTierStates((prev) => ({
        ...prev,
        [variantId]: (prev[variantId] ?? []).map((t) =>
          t.minQty === minQty ? { ...t, [field]: value } : t,
        ),
      }));
    },
    [],
  );

  const applyGlobalMargin = useCallback(() => {
    if (!globalMargin) return;
    setTierStates((prev) => {
      const next: Record<number, VariantTierState[]> = {};
      for (const [key, tiers] of Object.entries(prev)) {
        next[Number(key)] = tiers.map((t) => ({ ...t, margin: globalMargin, directPrice: "" }));
      }
      return next;
    });
  }, [globalMargin]);

  const copyFromFirst = useCallback(() => {
    if (variants.length < 2) return;
    const firstId = variants[0].id;
    setTierStates((prev) => {
      const firstTiers = prev[firstId] ?? [];
      const next = { ...prev };
      for (const v of variants.slice(1)) {
        next[v.id] = (prev[v.id] ?? []).map((t) => {
          const matching = firstTiers.find((ft) => ft.minQty === t.minQty);
          return matching ? { ...t, margin: matching.margin, directPrice: matching.directPrice } : t;
        });
      }
      return next;
    });
  }, [variants]);

  const variantTiersData = useMemo(() => {
    return variants.map((v, vIndex) => {
      const tiers = tierStates[v.id] ?? [];
      const tiersWithPrice = tiers.map((t) => {
        const marginVal = parseFloat(t.margin);
        const directVal = parseFloat(t.directPrice);
        let computedPrice: number | null = null;
        let displayMargin: number | null = null;

        if (!isNaN(directVal) && directVal > 0) {
          computedPrice = directVal;
          if (t.supplierCost != null && t.supplierCost > 0) {
            displayMargin = Math.round(((directVal - t.supplierCost) / t.supplierCost) * 10000) / 100;
          }
        } else if (!isNaN(marginVal) && marginVal > 0 && t.supplierCost != null && t.supplierCost > 0) {
          computedPrice = calcPrice(t.supplierCost, marginVal, roundUp);
          displayMargin = marginVal;
        }

        return { ...t, computedPrice, displayMargin };
      });

      // `[].every(...)` is true, so a variant with no seeded tier state would
      // count as fully configured and then save `pricingTiers: []`, wiping the
      // variant's existing tiers.
      const allConfigured =
        tiersWithPrice.length > 0 &&
        tiersWithPrice.every((t) => t.computedPrice != null && t.computedPrice > 0);
      const configuredCount = tiersWithPrice.filter((t) => t.computedPrice != null && t.computedPrice > 0).length;

      return {
        variant: v,
        label: variantLabel(v, vIndex),
        tiers: tiersWithPrice,
        allConfigured,
        configuredCount,
      };
    });
  }, [variants, tierStates, roundUp]);

  const allReady = variantTiersData.every((vd) => vd.allConfigured);
  const totalConfigured = variantTiersData.reduce((s, vd) => s + vd.configuredCount, 0);
  const totalTiers = variantTiersData.reduce((s, vd) => s + vd.tiers.length, 0);

  const handleSave = async () => {
    if (!allReady) return;
    setIsSubmitting(true);
    try {
      const payload: UpdateVariantPricingPayload[] = variantTiersData.map((vd) => ({
        variantId: vd.variant.id,
        pricingTiers: vd.tiers.map((t) => ({
          minQty: t.minQty,
          price: t.computedPrice!,
        })),
      }));
      await updateMyProductPricing(templateId, { variants: payload });
      showSuccessToast("Buyer pricing updated successfully");
      mutate((key) => typeof key === "string" && (
        key.startsWith("admin-product-details-") ||
        key.startsWith("product-") ||
        key.startsWith("admin-product-")
      ));
    } catch (err: unknown) {
      showErrorToast(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          "Failed to update pricing",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="rounded-2xl border border-borderDefault bg-bgWhite shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)]">
      <div className="px-6 pt-5 pb-4 max-md:px-4">
        <Heading variant="h3" tone="dark">Buyer-Facing Pricing</Heading>
        <Text variant="body" tone="body">
          Set the prices buyers see for each variant. Enter a margin % (calculated from supplier cost) or type a direct price.
        </Text>
      </div>

      <div className="border-t border-borderLight px-6 py-4 flex flex-col gap-6 max-md:px-4">
        {/* Controls */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Input
              name="global-margin"
              type="number"
              min={0}
              value={globalMargin}
              onChange={(e) => setGlobalMargin(e.target.value)}
              placeholder="e.g. 15"
              rightAddon={<Text variant="bodySm" tone="muted">%</Text>}
              inputWrapperClassName="!h-auto !py-2 !rounded-lg w-28"
            />
            <Button
              variant="primary"
              size="sm"
              leftIcon={<IconPercentage size={16} />}
              onClick={applyGlobalMargin}
              disabled={!globalMargin}
            >
              Apply margin to all
            </Button>
          </div>
          <div className="flex items-center gap-3">
            {variants.length > 1 && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<IconCopy size={16} />}
                onClick={copyFromFirst}
              >
                Copy from first variant
              </Button>
            )}
            <div className="flex items-center gap-2">
              <Switch checked={roundUp} onChange={setRoundUp} aria-label="Round up prices" />
              <Text variant="bodySm" tone="dark">Round up to x9.99</Text>
            </div>
          </div>
        </div>

        {/* Per-variant sections */}
        {variantTiersData.map((vd) => (
          <div key={vd.variant.id} className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Heading variant="h3" tone="dark">{vd.label}</Heading>
              {!vd.allConfigured && (
                <Text variant="bodyXs" tone="muted">
                  ({vd.configuredCount}/{vd.tiers.length} tiers set)
                </Text>
              )}
            </div>

            <div className="rounded-xl border border-borderDefault overflow-hidden">
              {/* Phones: keep the tier columns readable and scroll sideways. */}
              <div className="overflow-x-auto">
              <div className="max-md:min-w-[640px]">
              <div className="grid grid-cols-[1.5fr_1.2fr_1.2fr_1.2fr_1.5fr] gap-4 bg-bgLight px-5 py-3">
                <Text variant="bodySm" tone="muted">Tier</Text>
                <Text variant="bodySm" tone="muted">Supplier Cost</Text>
                <Text variant="bodySm" tone="muted">Margin (%)</Text>
                <Text variant="bodySm" tone="muted">Direct Price</Text>
                <Text variant="bodySm" tone="muted" className="text-right">Buyer Price</Text>
              </div>

              {vd.tiers.map((tier) => (
                <div
                  key={tier.minQty}
                  className="grid grid-cols-[1.5fr_1.2fr_1.2fr_1.2fr_1.5fr] gap-4 items-center px-5 py-4 border-t border-borderLight"
                >
                  <div className="flex flex-col gap-0.5">
                    <Text variant="bodyBold" tone="dark">{getTierLabel(tier.minQty)}</Text>
                    {tier.currentPrice != null && (
                      <Text variant="bodyXs" tone="muted">
                        Current: QAR {tier.currentPrice.toLocaleString()}
                      </Text>
                    )}
                  </div>

                  <Text variant="bodyBold" tone="dark">
                    {tier.supplierCost != null ? `QAR ${tier.supplierCost.toLocaleString()}` : "N/A"}
                  </Text>

                  <Input
                    name={`margin-${vd.variant.id}-${tier.minQty}`}
                    type="number"
                    min={0}
                    value={tier.margin}
                    onChange={(e) => {
                      updateTier(vd.variant.id, tier.minQty, "margin", e.target.value);
                      if (e.target.value) updateTier(vd.variant.id, tier.minQty, "directPrice", "");
                    }}
                    placeholder="e.g. 15"
                    rightAddon={<Text variant="bodySm" tone="muted">%</Text>}
                    inputWrapperClassName="!h-auto !py-2 !rounded-lg w-28"
                  />

                  <Input
                    name={`price-${vd.variant.id}-${tier.minQty}`}
                    type="number"
                    min={0}
                    value={tier.directPrice}
                    onChange={(e) => {
                      updateTier(vd.variant.id, tier.minQty, "directPrice", e.target.value);
                      if (e.target.value) updateTier(vd.variant.id, tier.minQty, "margin", "");
                    }}
                    placeholder="QAR"
                    inputWrapperClassName="!h-auto !py-2 !rounded-lg w-28"
                  />

                  <div className="flex justify-end">
                    {tier.computedPrice != null && tier.computedPrice > 0 ? (
                      <div className="inline-flex flex-col items-center rounded-xl bg-accentGreenTintBg px-4 py-2 w-fit">
                        <Text variant="bodyBold" tone="green">
                          QAR {tier.computedPrice.toLocaleString()}
                        </Text>
                        {tier.displayMargin != null && (
                          <Text variant="bodyXsBold" tone="green">
                            +{tier.displayMargin.toLocaleString(undefined, { maximumFractionDigits: 2 })}% over cost
                          </Text>
                        )}
                      </div>
                    ) : (
                      <Text variant="body" tone="muted">—</Text>
                    )}
                  </div>
                </div>
              ))}
              </div>
              </div>
            </div>
          </div>
        ))}

        {/* Warning */}
        {!allReady && (
          <div className="flex items-center gap-2 rounded-xl bg-accentYellowTint px-4 py-3">
            <span className="text-accentAmber shrink-0">●</span>
            <Text variant="bodySm" tone="amber">
              {totalConfigured} of {totalTiers} tiers across {variants.length} variant{variants.length !== 1 ? "s" : ""}{" "}
              {totalConfigured === 1 ? "has" : "have"} pricing set. All tiers must be configured before you can save.
            </Text>
          </div>
        )}

        {/* Save */}
        <div className="flex items-center gap-3 pt-1">
          <Button
            variant="primary"
            size="md"
            leftIcon={<IconDeviceFloppy size={18} />}
            onClick={handleSave}
            disabled={!allReady || isSubmitting}
          >
            {isSubmitting ? "Saving…" : "Save pricing"}
          </Button>
        </div>
      </div>
    </div>
  );
}
